import * as yup from 'yup';
import { Bell, Check, ChevronLeft } from 'react-feather';
import { useContext, useState } from 'react';
import { useForm } from 'react-form-state-manager';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { CheckoutCard } from './CheckoutPage';
import { CheckoutProps } from './CheckoutProvider';
import { EmbedContext } from '../Common/EmbedProvider';
import { Event } from './helpers';
import { emailRegex, getServerErrors } from '../../common/helpers';
import EmailInput from '../../common/ParticipantAttributes/Attributes/EmailInput';
import Layout, { SectionTitle, TitleBar } from '../Common/Layout';
import SaveInterestMutation from './SaveInterestMutation';
import UI from '../../common/UI';
import useMutation from '../../api/useMutation';
import useParticipantExistenceCheck from '../../common/useParticipantExistenceCheck';
import useProject from '../Common/useProject';
import useValidation from '../../common/useValidation';

const InterestPage = ({ event }: CheckoutProps) => {
  const { t } = useTranslation();
  const { embedded } = useContext(EmbedContext);
  const project = useProject();

  // Need to append search to all links because there might be a source URL in the URL
  const { search } = useLocation();

  const intro = event.description;

  const enabled = event.enable_interest_list && event.interest_list;

  return (
    <Layout event={{ ...event, project }} embedded={embedded}>
      {intro && (
        <>
          <UI.HR style={{ borderColor: 'rgba(0, 0, 0, 0.10)' }} />
          <UI.Div sc={{ color: 'gray.800' }}>
            <UI.HTML html={intro} />
          </UI.Div>
        </>
      )}

      <UI.Div>
        <TitleBar>
          <UI.Div>
            <UI.FadeIn>
              <UI.Link to={`/${event.id}${search}`} sc={{ small: true }}>
                <UI.Icon>
                  <ChevronLeft />
                </UI.Icon>
                {' '}
                {t('back')}
              </UI.Link>
            </UI.FadeIn>
          </UI.Div>
          <UI.Div>
            {t('interest_list.receive_updates')}
          </UI.Div>
          <UI.Div />
        </TitleBar>

        <CheckoutCard sc={{ hasPanels: true, active: true }}>
          <UI.FadeIn>
            {enabled && <InterestListForm event={event} />}
            {!enabled && (
              <UI.Legend sc={{ textAlign: 'center' }}>
                {t('interest_list.disabled_message')}
              </UI.Legend>
            )}
          </UI.FadeIn>
        </CheckoutCard>
      </UI.Div>
    </Layout>
  );
};

interface InterestListFormProps {
  event: Event;
}

const InterestListForm = ({ event }: InterestListFormProps) => {
  const { t } = useTranslation();

  // Need to append search to all links because there might be a source URL in the URL
  const { search } = useLocation();

  const [shownInterest, setShownInterest] = useState(false);

  const form = useForm({
    values: {
      email: '',
      first_name: '',
      last_name: '',
    },
  });

  const interestList = event.interest_list;

  const { participantExists, loading: checkingParticipant } = useParticipantExistenceCheck({
    participant: form.values,
  });

  const schema = yup.object().shape({
    email: yup.string().required().matches(emailRegex, { excludeEmptyString: true, name: 'email' }),
    first_name: participantExists ? yup.mixed().nullable() : yup.string().required(),
    last_name: participantExists ? yup.mixed().nullable() : yup.string().required(),
  });

  const [subscribe, { error, loading }] = useMutation(SaveInterestMutation, {
    variables: {
      input: {
        interest_list: interestList.id,
        ...form.values,
      },
    },
    onCompleted: () => {
      setShownInterest(true);
    },
  });

  const { valid, errors } = useValidation({
    schema,
    values: form.values,
    externalErrors: getServerErrors(error),
  });

  if (shownInterest) {
    return (
      <UI.FormGrid sc={{ textAlign: 'center' }}>
        <SectionTitle>
          <UI.Icon>
            <Check />
          </UI.Icon>
          {' '}
          {t('interest_list.subscribe_success')}
        </SectionTitle>

        <UI.Div>
          <UI.LinkButton to={`/${event.id}${search}`}>
            <UI.Icon>
              <ChevronLeft />
            </UI.Icon>
            {' '}
            {t('back')}
          </UI.LinkButton>
        </UI.Div>
      </UI.FormGrid>
    );
  }

  return (
    <UI.Form onSubmit={() => subscribe()}>
      <UI.FormGrid>
        <UI.Div>
          <SectionTitle sc={{ mb: 2 }}>
            {interestList.title}
          </SectionTitle>

          <UI.Paragraph sc={{ mb: 0 }}>
            {interestList.label}
          </UI.Paragraph>
        </UI.Div>

        <EmailInput
          value={form.values}
          onChange={(value) => form.set((contact) => ({ ...contact, ...value }))}
          onBlur={(key) => form.setTouched(key, true)}
          touched={form.touched}
          errors={errors}
          required
        />

        {!participantExists && (
          <UI.FormGrid sc={{ columns: ['1fr 1fr'] }}>
            <UI.InputGroup sc={{ valid: !errors.first_name, touched: form.touched.first_name }}>
              <UI.InputLabel htmlFor="FirstName">
                {t('common:participant_attributes.first_name')}
                {' '}
                <UI.RequiredMark />
              </UI.InputLabel>
              <UI.DebouncedInput {...form.text('first_name')} maxLength={100} id="FirstName" />
              <UI.ErrorMessages
                attribute={t('common:participant_attributes.first_name')}
                errors={errors.first_name}
              />
            </UI.InputGroup>
            <UI.InputGroup sc={{ valid: !errors.last_name, touched: form.touched.last_name }}>
              <UI.InputLabel htmlFor="LastName">
                {t('common:participant_attributes.last_name')}
                {' '}
                <UI.RequiredMark />
              </UI.InputLabel>
              <UI.DebouncedInput {...form.text('last_name')} maxLength={100} id="LastName" />
              <UI.ErrorMessages
                attribute={t('common:participant_attributes.last_name')}
                errors={errors.last_name}
              />
            </UI.InputGroup>
          </UI.FormGrid>
        )}

        <UI.Button
          type="submit"
          sc={{ brand: 'secondary', block: true, loading }}
          disabled={loading || !valid || checkingParticipant}
        >
          <UI.Icon>
            <Bell strokeWidth={2.5} />
          </UI.Icon>
          {' '}
          <UI.Strong sc={{ mr: 1 }}>
            {t('subscribe')}
          </UI.Strong>
        </UI.Button>
      </UI.FormGrid>
    </UI.Form>
  );
};

export default InterestPage;
